import React, { useEffect, useMemo, useState } from 'react';
import Button from '../components/Inputs/Button/Button';
import { PencilLine, Trash2, LayoutGrid, Table2, BarChart3 } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import styles from "./IndicatorsPage.module.css";
import GenericListView from '../views/GenericListView';
import FilterContainer from '../containers/FilterContainer';
import ModalContainer from '../containers/ModalContainer';
import useListController from '../hooks/useListController';
import useModalController from '../hooks/useModalController';
import useDialogCopy from '../hooks/useDialogCopy';
import {
    createIndicator,
    readIndicators,
    updateIndicator,
    deleteIndicator,
    listIndicatorCategories,
    listIndicatorFrequencies,
    listIndicatorUnits,
    readAllIndicators,
} from '../api/indicators';

export default function IndicatorsPage() {
    const [view, setView] = useState('explore');
    const [allIndicators, setAllIndicators] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('All');
    useEffect(() => { readAllIndicators().then(setAllIndicators).catch(() => setAllIndicators([])); }, []);

    const categoryStats = useMemo(() => {
        const counts = {};
        allIndicators.forEach(i => { const k = i.category || 'Uncategorised'; counts[k] = (counts[k] || 0) + 1; });
        return Object.entries(counts).map(([name,value]) => ({name,value})).sort((a,b)=>b.value-a.value);
    }, [allIndicators]);
    const visibleCards = useMemo(() => allIndicators.filter(i => selectedCategory === 'All' || (i.category || 'Uncategorised') === selectedCategory), [allIndicators, selectedCategory]);

    const controller = useListController({
        defaults: {
            page: 1,
            size: 10,
            sortBy: 'id',
            sortDir: 'asc',
            filters: { category: 'All', frequency: 'All' },
        },
        createFn: createIndicator,
        readFn: readIndicators,
        updateFn: updateIndicator,
        deleteFn: deleteIndicator,
    });

    const modalCtl = useModalController({
        create: controller.actions.create,
        update: controller.actions.update,
        remove: controller.actions.remove,
        mapEditValues: (row) => ({
            name: row.name ?? "",
            description: row.description ?? "",
            category: row.category ?? "",
            frequency: row.frequency ?? "",
            unit: row.unit ?? "",
        }),
    });

    const columns = useMemo(() => ([
        { key: 'id', label: 'Indicator ID' },
        { key: 'name', label: 'Name' },
        { key: 'description', label: 'Description' },
        { key: 'unit', label: 'Unit' },
        { key: 'frequency', label: 'Frequency' },
        { key: 'category', label: 'Category' },
    ]), []);

    const filterConfig = useMemo(() => ([
        { key: 'category', label: 'Category', type: 'select', allValue: 'All', loadOptions: listIndicatorCategories },
        { key: 'frequency', label: 'Frequency', type: 'select', allValue: 'All', loadOptions: listIndicatorFrequencies },
    ]), []);

    const header = useMemo(() => (
        <FilterContainer
            filters={filterConfig}
            state={controller.state}
            setFilter={controller.actions.setFilter}
            setSearch={controller.actions.setSearch}
            fetcher={readIndicators}
            search={{ key: 'q', label: 'Search name/description' }}
        />
    ), [controller.state, controller.actions.setFilter, controller.actions.setSearch, filterConfig]);

    const indicatorFields = useMemo(() => ([
        { key: "name", label: "Name", type: "input", required: true },
        { key: "description", label: "Description", type: "input", required: true },
        { key: "unit", label: "Unit", type: "select", required: true, loadOptions: listIndicatorUnits },
        { key: "frequency", label: "Frequency", type: "select", required: true, loadOptions: listIndicatorFrequencies },
        { key: "category", label: "Category", type: "select", required: true, loadOptions: listIndicatorCategories },
    ]), []);

    const actionCell = (row) => (
        <>
            <Button
                variant="warning"
                shape="square"
                leadingIcon={<PencilLine size={18} />}
                onClick={() => modalCtl.actions.openEdit(row)}
            >
                Edit
            </Button>
            <Button
                variant="danger"
                shape="square"
                leadingIcon={<Trash2 size={18} />}
                onClick={() => modalCtl.actions.openDelete(row)}
            >
                Delete
            </Button>
        </>
    );

    const actions = (
        <Button variant="primary" shape="pill" onClick={modalCtl.actions.openCreate}>
            Add New
        </Button>
    );

    const { open, mode, entity, values } = modalCtl.modal;
    const copy = useDialogCopy({ entityLabel: "Indicator", mode, entity });
    const fields = useMemo(() => (mode === "delete" ? [] : indicatorFields), [mode, indicatorFields]);

    return (
        <>
            <div className={styles.explorer}>
                <div className={styles.explorerHead}><div><h2>Indicator explorer</h2><p>Explore the catalogue visually by type, frequency and unit.</p></div><div className={styles.viewSwitch}><button className={view==='explore'?styles.selected:''} onClick={()=>setView('explore')}><LayoutGrid size={16}/> Explore</button><button className={view==='table'?styles.selected:''} onClick={()=>setView('table')}><Table2 size={16}/> Table</button></div></div>
                {view === 'explore' && <>
                  <div className={styles.categoryChips}><button className={selectedCategory==='All'?styles.chipActive:''} onClick={()=>setSelectedCategory('All')}>All <b>{allIndicators.length}</b></button>{categoryStats.map(c=><button key={c.name} className={selectedCategory===c.name?styles.chipActive:''} onClick={()=>setSelectedCategory(c.name)}>{c.name} <b>{c.value}</b></button>)}</div>
                  <div className={styles.visualGrid}><div className={styles.chartPanel}><div className={styles.panelTitle}><BarChart3 size={18}/> Indicators by type</div><ResponsiveContainer width="100%" height={Math.max(220,categoryStats.length*38)}><BarChart data={categoryStats} layout="vertical" margin={{left:10,right:20}}><CartesianGrid strokeDasharray="3 3"/><XAxis type="number" allowDecimals={false}/><YAxis type="category" dataKey="name" width={125}/><Tooltip/><Bar dataKey="value" onClick={d=>d?.name&&setSelectedCategory(d.name)}/></BarChart></ResponsiveContainer></div>
                  <div className={styles.cardsPanel}>{visibleCards.map(i=><button key={i.id} className={styles.indicatorCard} onClick={()=>modalCtl.actions.openEdit(i)}><div><span className={styles.category}>{i.category||'Uncategorised'}</span><h3>{i.name}</h3><p>{i.description||'No description'}</p></div><div className={styles.meta}><span>{i.frequency||'No frequency'}</span><span>{i.unit||'No unit'}</span></div></button>)}</div></div>
                </>}
            </div>
            {view === 'table' &&
            <GenericListView
                title="Indicators"
                subtitle="View, search, filter and add the different indicators available in the system"
                actions={actions}
                header={header}
                columns={columns}
                tableProps={controller.tableProps}
                paginationProps={controller.paginationProps}
                actionCell={actionCell}
                actionHeader="Actions"
            />
            }
            <ModalContainer
                open={open}
                mode={mode}
                title={copy.title}
                subtitle={copy.subtitle}
                yesLabel={copy.yesLabel}
                yesVariant={copy.yesVariant}
                fields={fields}
                values={values}
                setValue={modalCtl.actions.setValue}
                onNo={modalCtl.actions.close}
                onYes={modalCtl.actions.commit}
            />
        </>
    );
}
